/**
 * In-app help: one short, plain-English text per view, shown by the help button in the page header. English only for
 * now (the German UI shows it in English too). Keep each topic short: what the page is for, what you see, what you can
 * do. The big picture lives on the "How it works" page (/help).
 */
export interface HelpSection {
  heading: string;
  /** Paragraphs. */
  body?: string[];
  /** A short list (rendered as bullets, or numbered when `numbered`). */
  items?: string[];
  numbered?: boolean;
}

export interface HelpTopic {
  title: string;
  intro: string;
  sections: HelpSection[];
}

const topics = {
  tenants: {
    title: 'Tenants',
    intro:
      'A tenant is one agency (one customer) that uses jd agentic. Everything is kept separate per tenant: its agents, conversations, contacts, settings and users. One tenant never sees another tenant’s data.',
    sections: [
      {
        heading: 'What you see here',
        items: [
          'Name and slug: the tenant’s name and its short, fixed ID (for example muster-ag).',
          'Time zone and languages: when agents may write to people, and in which languages.',
          'jd enterprise: “Verified” means jd agentic can reach this tenant’s data in jd enterprise. “Not verified” means the connection is not set up or does not work yet.',
          'Status: Active tenants run normally. Suspended tenants are paused. Archived tenants are kept for the record only.',
        ],
      },
      {
        heading: 'Add a tenant',
        body: ['Click “New tenant” and enter a name, a slug, the time zone and the languages. Then open the tenant to finish the set-up.'],
      },
      {
        heading: 'Set-up checklist for a new tenant',
        numbered: true,
        items: [
          'jd enterprise: connect the tenant’s API account, so agents can read and save data.',
          'Channels: connect WhatsApp, SMS or email, so agents can talk to people.',
          'Users: add the people who review and approve what agents collect.',
          'Settings: set quiet hours and who gets notified.',
        ],
      },
      {
        heading: 'Who sees this page',
        body: [
          'Only platform admins see the list of all tenants and can add new ones. A tenant admin sees only their own tenant, under “Organisation”.',
        ],
      },
    ],
  },

  tenant: {
    title: 'Tenant settings',
    intro:
      'Everything one tenant needs to work: its connection to jd enterprise, the channels agents use to talk to people, its users and its rules. The tabs go from basic set-up (left) to fine-tuning (right).',
    sections: [
      {
        heading: 'General (platform admins)',
        body: ['Name, time zone and languages. “Model residency” decides where the AI models may run: anywhere, or only in the EU and Switzerland.'],
      },
      {
        heading: 'jd enterprise',
        body: [
          'The tenant’s own API account for jd enterprise. Agents only ever read and save data through this account, and only what its permissions (scopes) allow. “Verify” checks that jd enterprise accepts the account.',
        ],
      },
      {
        heading: 'Channels',
        body: [
          'How agents reach people: WhatsApp (the main channel), SMS (a fallback) and email. Each channel needs its own account details. “Verify” sends a test request to check them.',
        ],
      },
      {
        heading: 'WhatsApp consent',
        body: [
          'Who agreed to, or refused, WhatsApp messages. Someone who refused (for example by replying STOP) is never messaged on WhatsApp again.',
        ],
      },
      {
        heading: 'Users',
        body: [
          'The people who can sign in for this tenant, and their roles. For example, reviewers approve what agents collected, and admins can change settings. Ask every user to turn on two-factor sign-in on their profile page.',
        ],
      },
      {
        heading: 'Capabilities and Model policy (platform admins)',
        body: [
          'Capabilities are switches that decide what the tenant may change itself, for example whether it may build its own agents or use the AI designer. Model policy decides which AI model is used for which kind of work; leave it empty to use the platform defaults.',
        ],
      },
      {
        heading: 'Settings',
        body: [
          'Quiet hours (no new messages at night, and optionally not at weekends), who gets notified about review items and escalations, which users review, and the reply people get when they write without an open conversation.',
          'Test mode: while the list “Test mode: only these recipients” is not empty, agents send messages only to these numbers and email addresses; everyone else is skipped. Use it when agents work on test data that holds real people’s contacts. A yellow “Test mode” badge shows at the top while it is on; empty the list to switch it off.',
        ],
      },
    ],
  },

  rulesets: {
    title: 'Rulesets',
    intro:
      'A ruleset is a list of fixed checks that an agent runs on the data it collected, for example “an AHV number has 13 digits and starts with 756”. The checks are plain rules, not AI, so they always give the same answer.',
    sections: [
      {
        heading: 'What you see here',
        items: [
          'Left: all rulesets. Built-in ones (with a lock) are part of the platform, for example Swiss AHV, IBAN or permit checks. You can look at and test them, but not change them.',
          'Platform rulesets are shared by all tenants; tenant rulesets belong to one tenant only.',
          'Right: the rules of the selected ruleset, and a tester.',
        ],
      },
      {
        heading: 'Rules',
        items: [
          '“Error (blocks)”: the value is wrong, and the agent asks the person again.',
          '“Flag (review)”: the value is accepted but marked, so the reviewer looks at it closely.',
          'Path says which field the rule checks; “When” is an optional condition; “Assert” is the check itself; “Message” is what is shown when the check fails.',
        ],
      },
      {
        heading: 'Test a ruleset',
        body: [
          'Paste a sample as JSON and click “Run”. You see “OK”, or the errors and flags with the field they belong to. Nothing is saved or sent.',
        ],
      },
      {
        heading: 'Who can change rulesets',
        body: [
          'Admins and builders can create and edit their tenant’s rulesets; only platform admins can change platform rulesets. Tenant ruleset keys must not start with “ch.” (that prefix is reserved for the platform). Every change is in the audit log.',
        ],
      },
    ],
  },

  tools: {
    title: 'Tool registry',
    intro:
      'Tools are the actions agents may use in jd enterprise, for example “find contacts without AHV number” or “save the AHV number”. Almost all come from the jd enterprise API (jde-napi) description; this page decides which of them agents may use.',
    sections: [
      {
        heading: 'What the badges mean',
        items: [
          '“none”: the tool only reads data.',
          '“staged”: the tool prepares a change that a person still has to confirm in jd enterprise.',
          '“authoritative” (red): the tool changes real data in jd enterprise directly.',
          '“not callable”: agents cannot use this tool at the moment.',
        ],
      },
      {
        heading: 'When an agent can use a tool',
        body: [
          'Only when it is switched on (“Exposed to agents”), is not an internal tool, and, if it changes real data (authoritative), a platform admin has also switched on “Authoritative writes whitelisted”. Even then, built-in safety rules still block tools that promote or approve things, and agents only save data after a person approved it in the review queue.',
        ],
      },
      {
        heading: 'Import from OpenAPI',
        body: [
          'Reads the current jd enterprise API description again and shows what was added, removed or changed. Your switches and texts are kept.',
        ],
      },
      {
        heading: 'Who sees this page',
        body: ['Only platform admins. Every change is recorded in the audit log.'],
      },
    ],
  },

  audit: {
    title: 'Audit log',
    intro:
      'A record of everything that changed: settings, agents, deployments, review decisions, take-overs, and every value an agent saved in jd enterprise. Entries can never be edited or deleted.',
    sections: [
      {
        heading: 'What you see here',
        items: [
          'When: the time of the change (your local time).',
          'Actor: who did it, a user, an agent, the system or an API key.',
          'Action and Resource: what was done, to what (for example agent.delete on an agent).',
          '“Show”: the values before and after the change. Secret values are never recorded.',
        ],
      },
      {
        heading: 'Find something',
        body: [
          'Fill in resource type, resource ID or actor ID (exact values) and click “Filter”. The newest entries come first; “Load more” shows older ones.',
        ],
      },
      {
        heading: 'Who sees this page',
        body: ['Platform admins and tenant admins. A tenant admin sees only their own tenant’s entries.'],
      },
    ],
  },

  profile: {
    title: 'Your profile',
    intro: 'Your account, your password and two-factor sign-in.',
    sections: [
      {
        heading: 'Password',
        body: [
          'Enter your current password once and the new one twice (at least 12 characters). After the change you are signed out everywhere else; this browser stays signed in.',
        ],
      },
      {
        heading: 'Two-factor sign-in',
        body: [
          'Click “Set up 2FA”, scan the QR code with an authenticator app (Google Authenticator, Microsoft Authenticator, …) and enter the 6-digit code it shows. From then on you need a code from the app each time you sign in. Please turn it on.',
        ],
      },
    ],
  },

  dashboard: {
    title: 'Dashboard',
    intro: 'How the agents are doing, right now and over the last 30 days. Platform users see all tenants until they pick one in the top bar.',
    sections: [
      {
        heading: 'Right now',
        items: [
          'Runs today and active runs: how many agent runs started today, and how many are still going.',
          'Open reviews: items waiting for a person to approve. Red when one is overdue.',
          'Escalations: cases the agents handed over to your team.',
          'Failures today: runs that ended with an error. Red when there is at least one.',
          'Click a tile to open the matching list.',
        ],
      },
      {
        heading: 'Last 30 days',
        items: [
          'Completion rate: share of runs that reached their goal.',
          'Escalation and human-touch rate: how often a person had to step in.',
          'Model cost and cost per completed run, in CHF.',
          'Runs per day: hover a bar to see started, completed and failed runs.',
        ],
      },
    ],
  },

  agents: {
    title: 'Agents',
    intro: 'All agents of the selected tenant, and the templates (ready-made agents) they can start from.',
    sections: [
      {
        heading: 'What the badges mean',
        items: [
          '“Deployed vN”: version N is switched on and working. “Not deployed”: it exists but does not run.',
          '“Paused”: switched on, but stopped for now. “Disabled”: switched off.',
          'A template name with a version: the agent is based on that template. “Custom”: built from scratch.',
          'Templates: “Published vN” can be used by tenants; “Draft” is still being prepared.',
        ],
      },
      {
        heading: 'What you can do',
        body: [
          'Search by name, short name or tag. Click an agent or template to see its versions. “New agent” creates one from a template, from scratch, or from a description you write.',
        ],
      },
    ],
  },

  newAgent: {
    title: 'Create an agent',
    intro: 'Three ways to start. You can change everything later in the designer.',
    sections: [
      {
        heading: 'From a template (easiest)',
        body: ['Pick a published template and give the agent a name. It is created as a draft; nothing runs until you publish and deploy it.'],
      },
      {
        heading: 'Blank',
        body: [
          'Start with an empty agent and build the steps yourself in the designer. The tenant needs the “Author custom agents” capability for this. Platform admins can also create a platform template here.',
        ],
      },
      {
        heading: 'Describe it',
        body: ['Write in plain words what the agent should do; the AI drafts a complete agent that you then check and adjust in the designer.'],
      },
    ],
  },

  agentOverview: {
    title: 'Agent overview',
    intro: 'The versions of this agent and where it is deployed. Each change creates a new version; published versions never change.',
    sections: [
      {
        heading: 'Versions',
        items: [
          'Draft: work in progress, can be edited in the designer. There is at most one draft at a time.',
          'Published: tested and frozen; it can be deployed. “Deployed” marks the version that is live.',
          'Design / View opens the designer; Diff compares two versions; JSON shows the raw definition.',
          '“+” starts a new draft as a copy of that version.',
        ],
      },
      {
        heading: 'Buttons',
        items: [
          'Create draft: start a new version to change the agent.',
          'Publish: freeze the draft. It must have no errors and pass its test cases (evals) first.',
          'Deploy: switch a published version on for this tenant.',
          'Delete draft: only for agents that were never published or deployed and never ran.',
        ],
      },
    ],
  },

  templateOverview: {
    title: 'Template overview',
    intro: 'A template is a ready-made agent that tenants can start from. This page shows its versions.',
    sections: [
      {
        heading: 'Versions',
        items: [
          'Draft: work in progress. Published: tenants can use it. Withdrawn: no longer offered to tenants; agents already based on it keep working.',
          'Design / View opens the designer; Diff compares two versions; JSON shows the raw definition.',
        ],
      },
      {
        heading: 'Four-eyes rule',
        body: [
          'A template version needs a second platform admin: either another admin approves it, or another admin publishes it. The author alone cannot publish.',
        ],
      },
      {
        heading: 'Delete draft',
        body: ['Only templates that were never published can be deleted, and only by platform admins.'],
      },
    ],
  },

  designer: {
    title: 'Designer',
    intro: 'Here you build an agent as a flowchart: each box (node) is one step, the arrows say what comes next. Drafts save automatically.',
    sections: [
      {
        heading: 'The parts of the screen',
        items: [
          'Left: the building blocks. Drag a step or a jd enterprise tool onto the canvas (or double-click it).',
          'Middle: the flowchart. Connect steps by dragging from one dot to another.',
          'Right: the settings of what you selected. With nothing selected, the agent’s own settings: trigger, who it contacts, channels, allowed tools, limits.',
          'Bottom: Validation (problems to fix), Simulator (try the agent out), AI assistant and JSON.',
        ],
      },
      {
        heading: 'Try it out safely',
        body: [
          'The simulator plays a conversation with a pretend person. It never sends real messages and never writes to jd enterprise. You can save a good simulation as a test case.',
        ],
      },
      {
        heading: 'AI assistant',
        body: [
          'Describe a change in plain words (“add a reminder after 2 days”). The assistant shows a proposal on the canvas; nothing changes until you click Apply.',
        ],
      },
      {
        heading: 'Publish',
        body: [
          'The shield button shows what still blocks publishing: errors, test cases (evals) that did not pass, or, for templates, the missing second admin. Publish freezes the draft as a new version.',
        ],
      },
      {
        heading: 'Keyboard',
        items: ['Ctrl+Z undo, Ctrl+Shift+Z redo, Delete removes the selection, Ctrl+C / Ctrl+V copy and paste.'],
      },
    ],
  },

  evals: {
    title: 'Evals (test cases)',
    intro:
      'Test cases that check the agent behaves correctly before it is published, for example “a person who replies STOP is not contacted again”. They run with a pretend person and a fake AI, so they are fast and free.',
    sections: [
      {
        heading: 'Cases',
        body: [
          'Each case describes a situation and what must (or must not) happen: the expected outcome, limits on messages and cost, tools that must not be used. Create cases here, save them from the simulator, or let the AI assistant draft them.',
        ],
      },
      {
        heading: 'Run',
        body: [
          '“Run evals” runs all cases against this version. The results show which cases passed and why others failed. Publishing needs passing evals for exactly this version; if they are missing, publish runs them first.',
        ],
      },
    ],
  },

  deploy: {
    title: 'Deploy',
    intro: 'Switch a published version of this agent on, pause it, or go back to the previous version.',
    sections: [
      {
        heading: 'Before it goes live: checks',
        items: [
          'The version is published and valid for this tenant.',
          'The jd enterprise account has the permissions the agent needs.',
          'The WhatsApp message templates are approved and the channel works.',
          'Test cases (evals) passed and cost limits are set.',
          'Green: fine. Red: blocks the deploy. Amber: a warning that does not block.',
        ],
      },
      {
        heading: 'Options',
        body: ['Choose the version, an optional start time, and the branches it should work for (none selected means all branches).'],
      },
      {
        heading: 'Pause and roll back',
        body: [
          'Pause stops new runs; runs already going continue unless you also cancel them. Roll back switches to the previously deployed version. Only admins can deploy, pause and roll back.',
        ],
      },
    ],
  },

  runs: {
    title: 'Runs',
    intro: 'A run is one agent working on one case, for example one contact, from start to end.',
    sections: [
      {
        heading: 'Statuses',
        items: [
          'Pending, Running: starting or working right now.',
          'Waiting: waiting for a reply or a reminder time.',
          'In review: waiting for a person in the review queue. Escalated: handed over to your team.',
          'Completed: goal reached. Partial: ended without reaching the goal completely. Failed: ended with an error. Cancelled: stopped by a person.',
        ],
      },
      {
        heading: 'What you can do',
        body: [
          'Filter by status, agent, work item or date. “Include simulations” also shows test runs from the designer. Click a run to see every step it took. Platform users can select active runs and cancel them.',
        ],
      },
    ],
  },

  runTrace: {
    title: 'Run details',
    intro: 'Every step this run took, in order, with what went in and what came out.',
    sections: [
      {
        heading: 'Timeline',
        body: [
          'Each card is one step. Open it to see its input and output, the AI calls it made (model, tokens, cost) and any error. Failed steps are opened for you. Some details are hidden for viewers.',
        ],
      },
      {
        heading: 'Side panel',
        body: ['Status, outcome, cost, the work item (the case the run is about) and the values the run saved in jd enterprise.'],
      },
      {
        heading: 'Buttons',
        items: [
          'Open in designer: see the agent version this run used.',
          'Explain run: the AI explains what happened and can suggest a fix.',
          'Cancel run (admins): stop a run that is still active.',
          'Retry step (platform admins): run a failed step again.',
        ],
      },
    ],
  },

  metrics: {
    title: 'Metrics',
    intro: 'How well each agent and each branch did in a period, counting the runs that started in that period.',
    sections: [
      {
        heading: 'Columns',
        items: [
          'Completion: share of finished runs that reached their goal.',
          'Escalations: share of runs handed over to a person.',
          'Review edits: how often a reviewer had to correct what the agent collected.',
          'Human touch: how often a person had to do something at all (review or escalation).',
          'Cost per completed run (CHF) and median time to complete (hours).',
        ],
      },
      {
        heading: 'Export',
        body: ['The CSV buttons download the runs or the review items for your own analysis.'],
      },
    ],
  },

  conversations: {
    title: 'Conversations',
    intro: 'The messages between the agents and people. You can step in and write yourself at any time.',
    sections: [
      {
        heading: 'Badges',
        items: [
          '“24 h window open”: on WhatsApp you can write freely. When it is closed, only approved message templates can be sent, until the person writes again.',
          '“No opt-in”: the person has not agreed to WhatsApp messages.',
          '“Taken over”: a person is answering instead of the agent.',
          '“Unsolicited”: someone wrote without an open conversation.',
        ],
      },
      {
        heading: 'Take over and hand back',
        items: [
          'Take over: the agent stops writing and you answer yourself in the box at the bottom.',
          'Hand back to agent: the agent continues with the next message from the person and knows what you discussed.',
          'Close: ends the conversation.',
          'Only reviewers and admins can take over, and every step is in the audit log.',
        ],
      },
      {
        heading: 'Quiet hours',
        body: ['New messages, also yours, are held back during the tenant’s quiet hours and sent afterwards. Direct replies are not held back.'],
      },
    ],
  },

  review: {
    title: 'Review queue',
    intro:
      'What the agents collected (data, documents, timesheets) waits here for a person. Nothing is saved in jd enterprise before someone approves it.',
    sections: [
      {
        heading: 'Statuses',
        items: [
          'Open: nobody is working on it yet. In progress: assigned to someone.',
          'Decided: approved, corrected and approved, or rejected. Expired: the run ended before anyone decided.',
          'The SLA badge shows how much time is left: amber under 4 hours, red when overdue. Overdue items also create an escalation.',
        ],
      },
      {
        heading: 'What you can do',
        body: ['Filter by status, kind or agent, or show only overdue items. Click an item to decide.'],
      },
    ],
  },

  reviewDecision: {
    title: 'Review an item',
    intro: 'Check what the agent collected against the evidence, then decide.',
    sections: [
      {
        heading: 'The three panels',
        items: [
          'Evidence: the documents and the part of the conversation the data comes from.',
          'Proposed data: what will be saved, with checks (valid, errors, flags). You can correct values here.',
          'Context: the run, the agent and a link to the conversation.',
        ],
      },
      {
        heading: 'Decide',
        items: [
          'Approve (A): the data is saved in jd enterprise, in your name.',
          'Save & approve (E): save your corrections, then approve.',
          'Reject (R): nothing is saved; a comment is required.',
          'If jd enterprise cannot be reached, the decision is not recorded and the item stays open, so you can try again.',
        ],
      },
      {
        heading: 'Timesheets',
        body: [
          'Rule violations (breaks, overtime, Sunday or night work, …) block approval. If they are correct, tick “Accept the rule violations” and give a reason.',
        ],
      },
    ],
  },

  escalations: {
    title: 'Escalations',
    intro:
      'Cases the agents handed over to your team: someone wants a person, a complaint, a message that could not be delivered, an overdue review, a problem with jd enterprise, and so on.',
    sections: [
      {
        heading: 'Statuses',
        items: [
          'Open: new. Acknowledged: someone took it. Resolved: closed with an outcome.',
          'High severity comes first and also alerts the platform operators.',
        ],
      },
      {
        heading: 'What you can do',
        body: ['Open an escalation to see why it happened, take it on and decide how the run continues.'],
      },
    ],
  },

  escalation: {
    title: 'Escalation',
    intro: 'Why the agent handed this case over, and what should happen next.',
    sections: [
      {
        heading: 'Buttons',
        items: [
          'Assign to me: you take responsibility for it.',
          'Take over: you answer the person yourself in the conversation.',
          'Open conversation: read the messages.',
        ],
      },
      {
        heading: 'Resolve',
        items: [
          'Resume run: the agent continues where it stopped.',
          'Complete (partial): the run ends as partly done, with your note.',
          'Cancel run: the run stops and the conversation is closed.',
        ],
      },
    ],
  },
} satisfies Record<string, HelpTopic>;

export type HelpTopicId = keyof typeof topics;
export const helpTopics: Record<HelpTopicId, HelpTopic> = topics;
